import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

type BudgetSettingsDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  thresholdInput: string;
  onThresholdChange: (value: string) => void;
  savingThreshold: boolean;
  onSaveThreshold: () => void;
  onClearThreshold: () => void;
  autoApproveInput: string;
  onAutoApproveChange: (value: string) => void;
  savingAutoApprove: boolean;
  onSaveAutoApprove: () => void;
  onClearAutoApprove: () => void;
};

export function BudgetSettingsDialog({
  open,
  onOpenChange,
  thresholdInput,
  onThresholdChange,
  savingThreshold,
  onSaveThreshold,
  onClearThreshold,
  autoApproveInput,
  onAutoApproveChange,
  savingAutoApprove,
  onSaveAutoApprove,
  onClearAutoApprove,
}: BudgetSettingsDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Budget settings</DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          <div className="space-y-2">
            <p className="text-sm font-medium text-slate-900">Utilization alerts</p>
            <p className="text-xs text-slate-500">
              Notify budget managers when encumbered + expended reaches this percent of allocation.
            </p>
            <div className="flex flex-wrap items-end gap-2">
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">Threshold (%)</Label>
                <Input
                  className="h-9 w-24"
                  type="number"
                  min={1}
                  max={100}
                  placeholder="e.g. 80"
                  value={thresholdInput}
                  onChange={(e) => onThresholdChange(e.target.value)}
                />
              </div>
              <Button type="button" size="sm" onClick={onSaveThreshold} disabled={savingThreshold}>
                {savingThreshold ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Save
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={onClearThreshold}
                disabled={savingThreshold}
              >
                Disable
              </Button>
            </div>
          </div>

          <div className="space-y-2 border-t border-slate-100 pt-4">
            <p className="text-sm font-medium text-slate-900">Auto-approval on submit</p>
            <p className="text-xs text-slate-500">
              Auto-approve drafts when unit estimated cost is at or below this amount and the fund
              has enough available balance.
            </p>
            <div className="flex flex-wrap items-end gap-2">
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">Max unit cost (₦)</Label>
                <Input
                  className="h-9 w-32"
                  type="number"
                  min={0}
                  step="0.01"
                  placeholder="e.g. 5000"
                  value={autoApproveInput}
                  onChange={(e) => onAutoApproveChange(e.target.value)}
                />
              </div>
              <Button
                type="button"
                size="sm"
                onClick={onSaveAutoApprove}
                disabled={savingAutoApprove}
              >
                {savingAutoApprove ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Save
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={onClearAutoApprove}
                disabled={savingAutoApprove}
              >
                Disable
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
